import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogCancelButton,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { DatePicker } from '@/components/core/date-picker/date-picker';
import { Label } from '@/components/ui-kit/label';
import { EmployeeOption, EmployeeSearchPicker } from '@/modules/shared/hr-lookups';
import { useForwardPeerReview } from '../../hooks/use-peer-requests';
import { PeerRequest } from '../../types/peer-request.types';

export interface ForwardPeerReviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  request: PeerRequest | null;
  managerId: string;
}

export const ForwardPeerReviewDialog = ({
  open,
  onOpenChange,
  request,
  managerId,
}: ForwardPeerReviewDialogProps) => {
  const { t } = useTranslation();
  const [targets, setTargets] = useState<EmployeeOption[]>([]);
  const [deadline, setDeadline] = useState('');
  const { mutateAsync: forward, isPending } = useForwardPeerReview();

  useEffect(() => {
    if (open) {
      setTargets([]);
      setDeadline('');
    }
  }, [open]);

  if (!request) return null;

  const handleForward = async () => {
    try {
      await forward({
        request,
        fromManagerId: managerId,
        toManagerIds: targets.map((target) => target.UserId),
        deadline: deadline || undefined,
      });
      onOpenChange(false);
    } catch {
      // surfaced by the mutation's onError toast
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>{t('FORWARD_PEER_REVIEW_FOR', { name: request.review.Name })}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>{t('FORWARD_TO_MANAGERS')}</Label>
            <EmployeeSearchPicker
              excludeIds={[managerId, ...targets.map((target) => target.UserId)]}
              onSelect={(employee) => setTargets((current) => [...current, employee])}
            />
            <div className="flex flex-wrap gap-2 pt-1">
              {targets.map((target) => (
                <span
                  key={target.UserId}
                  className="flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs"
                >
                  {target.DisplayName}
                  <button
                    type="button"
                    aria-label={t('REMOVE')}
                    onClick={() =>
                      setTargets((current) =>
                        current.filter((item) => item.UserId !== target.UserId)
                      )
                    }
                  >
                    <X className="size-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="forward-deadline">{t('DEADLINE')}</Label>
            <DatePicker
              id="forward-deadline"
              value={deadline}
              onChange={setDeadline}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">{t('FORWARD_DEADLINE_HINT')}</p>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <DialogCancelButton onClick={() => onOpenChange(false)}>{t('CANCEL')}</DialogCancelButton>
          <Button
            type="button"
            onClick={handleForward}
            loading={isPending}
            disabled={isPending || targets.length === 0}
          >
            {t('FORWARD')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
